import {
	type EncryptedData,
	EncryptionError,
	EncryptionErrorType,
	type EncryptionStatus,
	useEncryptionStore,
} from './encryption'
import type { PbLineItemPackage } from '@/apiTypes'
import Plan, { PlanErrorType } from '@/classes/Plan'
import { OapProvider } from '@/data/oapProviders'
import { DocumentType } from '@/enums/DocumentType'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { getBlankEft } from '@/mixins/eft'
import { normalizeEmail } from '@/mixins/email'
import type { CrmIdentifiers } from '@/mixins/planVersionsV2'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { deepToRaw } from '@/services/deepToRaw'
import { useSessionStore } from '@/stores/session'
import type {
	EverplansPayment,
	OapPayment,
	PlanBasicData,
	PlanData,
	PlanPayment,
	PlanSensitiveData,
} from '@/types'
import { PaymentType } from '@precoa/calculator-sdk'
import { captureException } from '@sentry/vue'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'

const localforage = localforageLib.createInstance({
	name: 'sessions',
	storeName: 'plans',
})

export interface StoredPlanData {
	version: undefined | 1 | 2
	basic: Partial<PlanBasicData> & Pick<PlanData, 'id'>
	vault: EncryptedData | null
}

/** Store to keep track of the working set of plans and interact with plan storage */
export const usePlanStore = defineStore('plan', () => {
	const encryption = useEncryptionStore()
	const cloudSyncService = useCloudSyncService()

	/**
	 * Active plans in memory.
	 * Watchers are automatically set up when this array changes.
	 */
	const plans = ref<Plan[]>([])

	/** Get a plan by id */
	function getPlan(id: string): Plan | undefined {
		return plans.value.find((plan) => plan.id === id)
	}

	/** Get multiple plans by their ids */
	function getPlans(ids: string[]): Plan[] {
		return plans.value.filter((plan) => ids.includes(plan.id))
	}

	/**
	 * Sets the active plans directly from an array of Plan objects.
	 * Watchers are automatically managed via the plans array watch.
	 */
	function setActivePlans(activePlans: Plan[]) {
		plans.value = activePlans
	}

	/**
	 * Loads plans by IDs and sets them as active.
	 * Gets plans from memory first, then loads any missing plans from storage.
	 */
	async function setActivePlanIds(ids: string[]) {
		const activePlans: Plan[] = []
		const missingIds: string[] = []

		// First, try to get plans from memory
		for (const id of ids) {
			const existingPlan = getPlan(id)
			if (existingPlan) {
				activePlans.push(existingPlan)
			} else {
				missingIds.push(id)
			}
		}

		// Load any missing plans from storage
		if (missingIds.length > 0) {
			const loadedPlans = await loadPlans(missingIds)
			activePlans.push(...loadedPlans)
		}

		// Set the active plans (maintaining the original order)
		const orderedPlans = ids
			.map((id) => activePlans.find((plan) => plan.id === id))
			.filter(Boolean) as Plan[]
		setActivePlans(orderedPlans)
	}

	async function hydrateStoredPlan(planData: StoredPlanData) {
		if (!planData.vault) {
			return Plan.deserialize(planData.version, {
				...planData.basic,
				encryptionStatus: 'unencrypted',
			})
		}

		try {
			// Decrypt vault
			const sensitiveJson = await encryption.decrypt(planData.vault)
			const sensitive = JSON.parse(sensitiveJson) as PlanSensitiveData

			// Use the decrypted plan
			return Plan.deserialize(planData.version, {
				...planData.basic,
				...sensitive,
				encryptionStatus: 'unencrypted',
			})
		} catch (error) {
			console.error('Error decrypting plan', error)
			let encryptionStatus: EncryptionStatus =
				EncryptionErrorType.DecryptionError

			// Could not decrypt this plan. Use the basic data and save the vault in memory
			if (error instanceof EncryptionError) {
				encryptionStatus = error.errorType
			}

			return Plan.deserialize(
				planData.version,
				{
					...planData.basic,
					encryptionStatus,
				},
				planData.vault
			)
		}
	}

	/** Retrieve a plan from storage */
	async function loadPlan(id: string): Promise<Plan> {
		const planData = await localforage.getItem<StoredPlanData>(id.toLowerCase())
		if (!planData) throw new Error(`Plan with id ${id} not found`)

		return await hydrateStoredPlan(planData)
	}

	/** Retrieve a plan's basic data from storage */
	async function loadPlanBasicData(
		id: string
	): Promise<Partial<PlanBasicData> & Pick<PlanData, 'id'>> {
		const planData = await localforage.getItem<StoredPlanData>(id.toLowerCase())
		if (!planData) throw new Error(`Plan with id ${id} not found`)

		return planData.basic
	}

	async function decryptVault(plan: Plan, secret?: string | undefined) {
		if (!plan.vault) throw new Error('Cannot decrypt: no vault')

		const sensitiveJson = await encryption.decrypt(plan.vault, secret)
		const sensitive = JSON.parse(sensitiveJson) as PlanSensitiveData

		plan.update({ ...sensitive, encryptionStatus: 'unencrypted' })
	}

	/** Retrieve multiple plans from storage */
	async function loadPlans(ids: string[]): Promise<Plan[]> {
		return Promise.all(
			ids.map(async (id) => {
				return loadPlan(id)
			})
		)
	}

	async function getNamesFromPlanIds(ids: string[]): Promise<string[]> {
		const names = []
		for (const planId of ids) {
			try {
				const plan = await loadPlan(planId)
				names.push(`${plan.firstName} ${plan.lastName}`)
			} catch (e: any) {
				names.push(`[could not load data for ${planId}]`)
				console.error(e.message)
			}
		}
		return names
	}

	/** Add a new plan to the store */
	async function addPlan(plan: Plan, active = false): Promise<void> {
		if (active) {
			plans.value.push(plan)
		}

		await storePlan(plan)
	}

	/** Update an existing plan with the given data */
	async function updatePlan(
		id: string,
		updates: Partial<PlanData>
	): Promise<void> {
		let plan: Plan | undefined = undefined

		// Look in memory
		plan = getPlan(id)

		if (!plan) {
			// Load from storage
			plan = await loadPlan(id)
		}

		if (!plan) {
			const e = new Error('Plan not found')
			captureException(e)
			throw e
		}

		plan.update(updates)
		await storePlan(plan)
	}

	/** Completely replace a plan with the given data */
	async function savePlan(plan: Plan): Promise<void> {
		// First, update in memory if we have it
		const existingPlan = getPlan(plan.id)
		if (existingPlan) {
			plans.value[plans.value.indexOf(existingPlan)] = plan
		}

		await storePlan(plan)
		await cloudSyncService.plan.registerForUpsert(plan.id)
	}

	/** Copy properties from one plan to another */
	function copyProperties<T extends keyof Plan>(
		source: Plan,
		target: Plan,
		fieldOrFields: T | T[]
	): void {
		const fields = Array.isArray(fieldOrFields)
			? fieldOrFields
			: [fieldOrFields]
		for (const field of fields) {
			target[field] = structuredClone(toRaw(source[field]))
		}
	}

	/** Delete a plan */
	async function deletePlan(id: string): Promise<void> {
		const sessionStore = useSessionStore()

		// Remove from memory
		plans.value = plans.value.filter((plan) => plan.id !== id)

		// Remove from active session if applicable
		const session = sessionStore.activeSession
		if (session) {
			session.planIds = session.planIds.filter((pid) => pid !== id)
		}

		// Remove from storage
		await localforage.removeItem(id)
	}

	/** Get all plan ids from storage */
	async function getSavedPlanIds(): Promise<string[]> {
		return await localforage.keys()
	}

	/** Delete everything from memory and local storage */
	async function purge(): Promise<any> {
		setActivePlans([])
		return localforage.clear()
	}

	async function updateSecret(plan: Plan) {
		await encryption.setSecret(plan.id, plan.socialSecurityNumber)
		await savePlan(plan)
	}

	/** Private method to physically store the plan data in IndexedDB */
	async function storePlan(plan: Plan): Promise<void> {
		let { basic, sensitive, vault } = plan.serialize()

		const serializedSensitive = JSON.stringify(toRaw(sensitive))
		basic = deepToRaw(toRaw(basic))

		// Try replacing the vault by encrypting the sensitive data
		try {
			vault = await encryption.encrypt(
				plan.id,
				serializedSensitive,
				plan.socialSecurityNumber || undefined
			)
		} catch (error) {
			console.error('Error encrypting plan', error)
			captureException(error)
		}

		await localforage.setItem<StoredPlanData>(plan.id, {
			basic,
			vault: toRaw(vault),
			version: 2,
		})
	}

	// reset enrollment and payment authorization steps, so plan holder can review the updated payment mode information in enrollment and payment authorization steps
	const resetDocumentFlowForPaymentChange = async (plan: Plan) => {
		const completedSteps = plan.completedSteps.filter(
			(step) =>
				step !== DocumentType.EnrollmentForm &&
				step !== DocumentType.PaymentAuthorization
		)
		await updatePlan(plan.id, {
			paymentStep: 'html',
			completedSteps,
		})
	}

	// reset payment type and details when insurance payment type changes
	const resetPaymentTypeAndDetails = (plan: Plan) => {
		let payment: PlanPayment = plan.payment

		switch (plan.insurance.paymentType) {
			case PaymentType.BILL:
				if (plan.payment.paymentType !== PlanPaymentType.DirectBill) {
					payment = {
						paymentType: PlanPaymentType.DirectBill,
						paymentDetails: {
							billingType: 'Monthly',
						},
					}
				}
				break
			case PaymentType.CREDIT_CARD:
				if (plan.payment.paymentType !== PlanPaymentType.CreditCard) {
					payment = {
						paymentType: PlanPaymentType.CreditCard,
						paymentDetails: {
							token: '',
						},
					}
				}
				break
			default:
				if (plan.payment.paymentType !== PlanPaymentType.EFT) {
					payment = {
						paymentType: PlanPaymentType.EFT,
						paymentDetails: {
							initialAccount: getBlankEft(),
							ongoingAccount: getBlankEft(),
							useDifferentOngoingAccount: false,
						},
					}
				}
				break
		}

		updatePlan(plan.id, {
			payment,
		})
	}

	const resetInsurancePaymentDetails = async (plan: Plan) => {
		let payment: PlanPayment

		switch (plan.payment.paymentType) {
			case PlanPaymentType.DirectBill:
				payment = {
					paymentType: PlanPaymentType.DirectBill,
					paymentDetails: {
						billingType: 'Monthly',
					},
				}
				break
			case PlanPaymentType.CreditCard:
				payment = {
					paymentType: PlanPaymentType.CreditCard,
					paymentDetails: {
						token: '',
					},
				}
				break
			default:
				payment = {
					paymentType: PlanPaymentType.EFT,
					paymentDetails: {
						initialAccount: getBlankEft(),
						ongoingAccount: getBlankEft(),
						useDifferentOngoingAccount: false,
					},
				}
				break
		}

		await updatePlan(plan.id, { payment })
	}

	const resetEverplansPaymentDetails = async (plan: Plan) => {
		let payment: EverplansPayment = plan.everplansPayment

		switch (plan.everplansPayment.paymentType) {
			case PlanEverplansPaymentType.EFT:
				payment = {
					...payment,
					paymentType: PlanEverplansPaymentType.EFT,
					paymentDetails: getBlankEft(),
				}
				break
			case PlanEverplansPaymentType.CreditCard:
				payment = {
					...payment,
					paymentType: PlanEverplansPaymentType.CreditCard,
					paymentDetails: {
						token: '',
					},
				}
				break
		}

		await updatePlan(plan.id, { everplansPayment: payment })
	}

	const resetOapPaymentDetails = async (plan: Plan) => {
		let payment: OapPayment = plan.oapPayment

		if (plan.oapPayment.provider === OapProvider.SEPIO_GUARD) {
			switch (plan.oapPayment.paymentPlan?.paymentType) {
				case PlanOapPaymentType.EFT:
					payment = {
						...payment,
						paymentPlan: {
							paymentType: PlanOapPaymentType.EFT,
							paymentDetails: getBlankEft(),
						},
					} as OapPayment
					break
				case PlanOapPaymentType.CreditCard:
					payment = {
						...payment,
						paymentPlan: {
							paymentType: PlanOapPaymentType.CreditCard,
							paymentDetails: {
								token: '',
							},
						},
					} as OapPayment
					break
			}
			await updatePlan(plan.id, { oapPayment: payment })
		}
	}

	/**
	 * Finds plans in local forage
	 * @param leadId
	 */
	const fetchByLeadId = async (leadId: string) => {
		const plans: StoredPlanData[] = []
		await localforage.iterate(function (localPlan: StoredPlanData) {
			if (localPlan.basic.leadId === leadId) {
				plans.push(localPlan)
			}
		})

		return plans
	}

	/**
	 * Finds plans match by lead/opportunity/contact, used by select plan versions
	 * @param identifiers
	 */

	const getByLeadIdOrOpportunityIdOrContactId = async (
		identifiers: CrmIdentifiers
	) => {
		const plans: StoredPlanData[] = []

		if (
			!identifiers.leadId &&
			!identifiers.opportunityId &&
			!identifiers.contactId
		) {
			return []
		}

		await localforage.iterate(function (localPlan: StoredPlanData) {
			// We only force lowercase on leadId because it is a UUID
			// The others are Salesforce IDs.
			if (
				(!!identifiers.leadId &&
					localPlan.basic.leadId?.toLowerCase() ===
						identifiers.leadId.toLowerCase()) ||
				(!!identifiers.opportunityId &&
					localPlan.basic.opportunityId === identifiers.opportunityId) ||
				(!!identifiers.contactId &&
					localPlan.basic.contactId === identifiers.contactId)
			) {
				plans.push(localPlan)
			}
		})

		return plans
	}

	// Checks if a plan includes a package
	// Qualifications: item is not a bundle, the category is package and subcategory is not essentials
	const planIncludesPackage = (planId: string) => {
		const sessionStore = useSessionStore()
		const plan = getPlan(planId)

		const items =
			plan?.cartItems.map((cartItem) => ({
				...cartItem,
				lineItem: sessionStore.activePriceBook!.findLineItem(
					cartItem.lineItemId
				),
				subItems: cartItem.subItems.map((subItem) => ({
					...subItem,
					lineItem: sessionStore.activePriceBook!.findLineItem(
						subItem.lineItemId
					),
				})),
			})) ?? []

		return items.some((item) => {
			const lineItem = item.lineItem as PbLineItemPackage

			return (
				lineItem.category === PbProductType.Package &&
				lineItem.subcategory !== PbSubcategory.PackageEssentials &&
				!lineItem.isBundle &&
				item.isSoftDeleted === false
			)
		})
	}

	/**
	 * Whether a plan must wait its turn: a Premium sharing its email is still unsubmitted,
	 * and Everplans processes submissions FIFO, so that Premium has to go first.
	 *
	 * This asks "may this plan submit yet?", which is a question about ordering. For
	 * "is this email already taken?" use {@link findPlansClaimingEverplansEmail}.
	 * @param planToCheck
	 */
	const hasDuplicateEmailWithIncompletePremiums = (planToCheck: Plan) => {
		return plans.value.some((plan) => {
			return (
				plan.id !== planToCheck.id &&
				plan.isEverplansPremium() &&
				normalizeEmail(plan.everplansEmail) ===
					normalizeEmail(planToCheck.everplansEmail) &&
				plan.packetStatus === PlanPacketStatus.Incomplete
			)
		})
	}

	/**
	 * Whether a plan's Everplans account has claimed its email, so that no other plan in
	 * the session may enroll with it.
	 *
	 * A Premium always enrolls, so its email is reserved the moment it is entered. A Free
	 * Everplans only claims one once it has been submitted and Everplans accepted the
	 * address - until then nothing has been created with it.
	 */
	const hasClaimedItsEverplansEmail = (plan: Plan) => {
		if (plan.isEverplansPremium()) {
			return true
		}

		return (
			plan.packetStatus === PlanPacketStatus.Submitted &&
			!plan.errors.some((error) => error.type === PlanErrorType.EverplansEmail)
		)
	}

	/**
	 * The plans in this session whose Everplans account already claims the given email.
	 *
	 * Everplans requires a distinct email per account, so this is the single answer to
	 * "is this email taken?" - shared by EverplansStep, where the email is first entered,
	 * UpdateEmailModal, where it is edited, and the warning icon on the Finalize step, so
	 * that the three cannot disagree.
	 *
	 * Returns the plans rather than a boolean so callers can name them in an error message.
	 * @param planToCheck the plan the email would be saved to, excluded from the results
	 * @param email defaults to the plan's current Everplans email
	 */
	const findPlansClaimingEverplansEmail = (
		planToCheck: Plan,
		email: string | undefined = planToCheck.everplansEmail
	) => {
		const emailToCheck = normalizeEmail(email)

		// A blank email is never a duplicate - nobody is enrolling with it
		if (!emailToCheck) {
			return []
		}

		return plans.value.filter(
			(plan) =>
				plan.id !== planToCheck.id &&
				normalizeEmail(plan.everplansEmail) === emailToCheck &&
				hasClaimedItsEverplansEmail(plan)
		)
	}

	const copyPolicyEmailToEverplansEmailForPlanHolder = async (plan: Plan) => {
		// If the plan has no policy email or the everplans email is already set, do nothing
		if (!plan.email || plan.everplansEmail) {
			return
		}

		await updatePlan(plan.id, {
			everplansEmail: plan.email,
		})
	}

	return {
		copyProperties,
		plans,
		loadPlan,
		loadPlanBasicData,
		loadPlans,
		getPlan,
		getPlans,
		getNamesFromPlanIds,
		fetchByLeadId,
		getByLeadIdOrOpportunityIdOrContactId,
		addPlan,
		updatePlan,
		updateSecret,
		decryptVault,
		deletePlan,
		savePlan,
		setActivePlans,
		setActivePlanIds,
		getSavedPlanIds,
		purge,
		resetDocumentFlowForPaymentChange,
		resetPaymentTypeAndDetails,
		resetInsurancePaymentDetails,
		resetEverplansPaymentDetails,
		resetOapPaymentDetails,
		hydrateStoredPlan,
		planIncludesPackage,
		hasDuplicateEmailWithIncompletePremiums,
		findPlansClaimingEverplansEmail,
		copyPolicyEmailToEverplansEmailForPlanHolder,
	}
})
